// src/component/UIVisibilityAdmin.jsx
import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Button,
  Checkbox,
  TextField,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

const DEFAULTS = {
  enabled: true,
  allowAdminBypass: true,
  maxPaymentPhase: 3, // ★ 1期〜◯期まで表示
  allowedPaymentPhases: [3], // ★ 許可する期（配列）

  // ★追加：セカイムラ認証ユーザにも交流を開放する
  allowSekaiMura: false,
};

export default function UIVisibilityAdmin() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  // 設定 state
  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [allowAdminBypass, setAllowAdminBypass] = useState(
    DEFAULTS.allowAdminBypass
  );
  const [maxPaymentPhase, setMaxPaymentPhase] = useState(
    DEFAULTS.maxPaymentPhase
  );
  const [allowedPaymentPhases, setAllowedPaymentPhases] = useState(
    DEFAULTS.allowedPaymentPhases
  );

  // ★追加
  const [allowSekaiMura, setAllowSekaiMura] = useState(DEFAULTS.allowSekaiMura);

  const showSnack = useCallback((message, type = "success") => {
    setSnackbar({ open: true, message, type });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const normalizeMax = (value) => {
    const n = Math.floor(Number(value));
    if (!Number.isFinite(n) || n < 1) return 1;
    // あり得ない上限に暴走しないための保険（必要なら変更OK）
    return Math.min(n, 50);
  };

  // max を変更したとき、allowed を max 以下に揃える
  const handleChangeMax = (value) => {
    const n = normalizeMax(value);
    setMaxPaymentPhase(n);
    setAllowedPaymentPhases((prev) =>
      (prev || [])
        .map((v) => Number(v))
        .filter((p) => Number.isFinite(p) && p >= 1 && p <= n)
        .sort((a, b) => a - b)
    );
  };

  // 設定ドキュメント購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", "communityChatVisibility");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const merged = { ...DEFAULTS, ...data };

        const safeMax = normalizeMax(merged.maxPaymentPhase);
        setEnabled(merged.enabled !== false);
        setAllowAdminBypass(merged.allowAdminBypass !== false);
        setMaxPaymentPhase(safeMax);

        // ★追加
        setAllowSekaiMura(merged.allowSekaiMura === true);

        const phasesRaw = Array.isArray(merged.allowedPaymentPhases)
          ? merged.allowedPaymentPhases
          : DEFAULTS.allowedPaymentPhases;

        const phases = phasesRaw
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n))
          .filter((n) => n >= 1 && n <= safeMax)
          .sort((a, b) => a - b);

        setAllowedPaymentPhases(phases.length ? phases : []);
        setLoading(false);
      },
      (err) => {
        console.error("UIVisibilityAdmin onSnapshot error:", err);
        setLoading(false);
        showSnack("設定の取得に失敗しました。", "error");
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSnack]);

  const togglePhase = (phase, on) => {
    setAllowedPaymentPhases((prev) => {
      const set = new Set((prev || []).map((v) => Number(v)));
      if (on) set.add(phase);
      else set.delete(phase);
      return Array.from(set)
        .filter((n) => Number.isFinite(n))
        .filter((n) => n >= 1 && n <= maxPaymentPhase)
        .sort((a, b) => a - b);
    });
  };

  // 保存
  const handleSave = async () => {
    try {
      const ref = doc(db, "systemConfig", "communityChatVisibility");
      const safeMax = normalizeMax(maxPaymentPhase);

      const payload = {
        enabled: Boolean(enabled),
        allowAdminBypass: Boolean(allowAdminBypass),
        maxPaymentPhase: safeMax,
        allowedPaymentPhases: (allowedPaymentPhases || [])
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n))
          .filter((n) => n >= 1 && n <= safeMax)
          .sort((a, b) => a - b),

        // ★追加
        allowSekaiMura: Boolean(allowSekaiMura),

        updatedAt: new Date(), // 後で serverTimestamp にしてもOK
      };

      await setDoc(ref, payload, { merge: true });
      showSnack("表示設定を保存しました。");
    } catch (err) {
      console.error("UIVisibilityAdmin save error:", err);
      showSnack("保存に失敗しました。時間をおいて再度お試しください。", "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN
        </Typography>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.03em",
            color: textBase,
          }}
        >
          表示関連（交流チャット）
        </Typography>

        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          AppShell の「交流」ダイアログで、県/地域/全国チャットの表示条件を管理します。
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        {/* セクションタイトル */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <VisibilityRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>

          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: textBase, mb: 0.5 }}
            >
              交流チャットの表示設定
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              県/地域/全国チャットを表示するか、どの「期」に許可するかを設定します。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                {enabled ? "現在：表示する（有効）" : "現在：表示しない（停止中）"}
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={allowAdminBypass}
                onChange={(e) => setAllowAdminBypass(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                管理者は条件を無視して表示する
              </Typography>
            }
          />

          {/* ★追加：セカイムラ認証ユーザにも交流を開放 */}
          <FormControlLabel
            control={
              <Switch
                checked={allowSekaiMura}
                onChange={(e) => setAllowSekaiMura(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                セカイムラ認証ユーザにも交流チャットを表示する
              </Typography>
            }
          />

          <Divider sx={{ my: 1 }} />

          <TextField
            label="表示する期の上限（◯期まで）"
            type="number"
            value={maxPaymentPhase}
            onChange={(e) => handleChangeMax(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            helperText="例：5 にすると 1期〜5期 のチェックが表示されます。"
            fullWidth
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ForumRoundedIcon sx={{ color: accent }} />
            <Typography sx={{ fontWeight: 800, color: textBase }}>
              閲覧できる期
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={0.5}
            sx={{ pl: 0.5, flexWrap: "wrap" }}
          >
            {Array.from(
              { length: normalizeMax(maxPaymentPhase) },
              (_, i) => i + 1
            ).map((phase) => {
              const checked = allowedPaymentPhases.includes(phase);
              return (
                <FormControlLabel
                  key={phase}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => togglePhase(phase, e.target.checked)}
                    />
                  }
                  label={`${phase}期`}
                />
              );
            })}
          </Stack>

          <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.65)" }}>
            ※ユーザーの payments[].paymentPhase（数値）と照合して、許可された期のみ表示します。
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                fontWeight: 800,
                px: 3,
                bgcolor: accent,
                boxShadow: "0 10px 24px rgba(20,16,40,0.22)",
                "&:hover": {
                  bgcolor: accent,
                  boxShadow: "0 14px 28px rgba(20,16,40,0.28)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              保存
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={handleSnackbarClose}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
